/**
 * Sensors Service - Irrigo Platform
 * Servicio para gestión de sensores y lecturas
 */

import { api, ApiResponse, PaginatedResponse, PaginationParams } from './api';
import type { 
  ApiSensor, 
  CreateSensorRequest, 
  UpdateSensorRequest,
  ApiSensorReading,
  ApiSoilData,
} from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

export interface SensorFilters extends PaginationParams {
  // CAMBIO v0.2.0: Filtrar por empresa en lugar de jardín
  companyId?: string;
  // Filtrar por zona (para obtener sensores asignados a una zona específica)
  zoneId?: string;
  status?: 'online' | 'offline' | 'warning';
  communicationType?: 'mqtt' | 'chirpstack';
  search?: string;
}

export interface ReadingsFilters extends PaginationParams {
  start?: string;
  end?: string;
}

// ============================================================================
// SENSORS SERVICE
// ============================================================================

export const sensorsService = {
  /**
   * Listar sensores
   */
  async getAll(params?: SensorFilters): Promise<PaginatedResponse<ApiSensor>> {
    return api.getPaginated<ApiSensor>('/sensors', params);
  },

  /**
   * Obtener sensor por ID
   */
  async getById(id: string): Promise<ApiResponse<ApiSensor>> {
    return api.get<ApiSensor>(`/sensors/${id}`);
  },

  /**
   * Crear nuevo sensor
   */
  async create(data: CreateSensorRequest): Promise<ApiResponse<ApiSensor>> {
    return api.post<ApiSensor>('/sensors', data);
  },

  /**
   * Actualizar sensor
   */
  async update(id: string, data: UpdateSensorRequest): Promise<ApiResponse<ApiSensor>> {
    return api.put<ApiSensor>(`/sensors/${id}`, data);
  },

  /**
   * Eliminar sensor
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/sensors/${id}`);
  },

  // ==========================================================================
  // READINGS
  // ==========================================================================

  /**
   * Obtener lecturas históricas del sensor
   * NOTA: El endpoint devuelve un array directo, no paginado según API_REFERENCE.md
   */
  async getReadings(sensorId: string, params?: ReadingsFilters): Promise<ApiResponse<ApiSensorReading[]>> {
    return api.get<ApiSensorReading[]>(`/sensors/${sensorId}/readings`, params);
  },

  /**
   * Obtener datos de suelo agregados
   */
  async getSoilData(sensorId: string, params?: { start?: string; end?: string }): Promise<ApiResponse<{ soilData: ApiSoilData[] }>> {
    return api.get<{ soilData: ApiSoilData[] }>(`/sensors/${sensorId}/soil-data`, params);
  },
};

export default sensorsService;
